import { useState } from "react";
import { CalendarClock, Download } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import {
  createPlannedAssignment,
  getContractorExportLog,
  getPlannedAssignmentContractors,
  getPlannedAssignments,
  withdrawPlannedAssignment,
  type PlannedAssignment,
} from "../api";
import { apiErr } from "../utils";

const SCOPE_OPTIONS = [
  { value: "wpw", label: "WPW — pozyskanie zgód" },
  { value: "mdcp_geodezja", label: "MDCP — geodezja" },
];

function monthLabel(month: string | null): string {
  if (!month) return "";
  const [year, mm] = month.split("-");
  const names = [
    "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
    "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
  ];
  return names[Number(mm) - 1] ? `${names[Number(mm) - 1]} ${year}` : month;
}

/** Pasek „Planowane do zlecenia" na karcie projektu ZE: aktywne zapowiedzi (z „Wycofaj"),
 *  przycisk oznaczania + licznik pobrań wykazów przez podwykonawców. Zapowiedź nie wysyła
 *  ŻADNYCH powiadomień — firma po prostu widzi projekt na swojej mapie. */
export function PlannedAssignmentStrip({ connectionId }: { connectionId: string }) {
  const qc = useQueryClient();
  const key = ["pp", "planned-assignments", connectionId] as const;
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => getPlannedAssignments(connectionId),
    staleTime: 30_000,
  });
  const { data: exportLog } = useQuery({
    queryKey: ["pp", "contractor-exports", connectionId],
    queryFn: () => getContractorExportLog(connectionId),
  });
  const [modalOpen, setModalOpen] = useState(false);

  const withdrawM = useMutation({
    mutationFn: withdrawPlannedAssignment,
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const assignments = data?.assignments ?? [];
  const active = assignments.filter((a) => a.is_active);
  const exports = exportLog?.exports ?? [];

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {active.map((assignment) => (
        <span
          key={assignment.id}
          className="flex items-center gap-2 border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white px-2.5 py-1 text-xs dark:bg-gray-900"
          title={assignment.note ? `Notatka wewnętrzna: ${assignment.note}` : undefined}
        >
          <CalendarClock size={13} className="text-[var(--color-primary)]" />
          <span>
            <b>Planowane do zlecenia ({assignment.scope_display}):</b>{" "}
            {assignment.contractor_name} · {monthLabel(assignment.expected_month)}
            {assignment.created_by_name && (
              <span className="text-gray-500 dark:text-gray-400">
                {" "}
                · oznaczył(a): {assignment.created_by_name}
              </span>
            )}
          </span>
          <button
            onClick={() => withdrawM.mutate(assignment.id)}
            disabled={withdrawM.isPending}
            className="ml-1 border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] dark:text-gray-300"
            title="Wycofaj zapowiedź — projekt natychmiast zniknie z mapy tej firmy"
          >
            Wycofaj
          </button>
        </span>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setModalOpen(true)}>
        <CalendarClock size={13} /> Oznacz jako planowane do zlecenia
      </Button>
      {exports.length > 0 && (
        <span
          className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"
          title={exports
            .slice(0, 8)
            .map(
              (row) =>
                `${new Date(row.created_at).toLocaleString("pl-PL")} — ${row.contractor_name}` +
                ` (${row.scope === "wpw" ? "WPW" : "MDCP"}, ${row.file_format}, ${row.row_count} wierszy)`,
            )
            .join("\n")}
        >
          <Download size={12} /> pobrania wykazów: {exports.length}
        </span>
      )}
      <PlannedAssignmentModal
        connectionId={connectionId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => {
          setModalOpen(false);
          qc.invalidateQueries({ queryKey: key });
        }}
      />
    </div>
  );
}

function PlannedAssignmentModal({
  connectionId,
  open,
  onClose,
  onCreated,
}: {
  connectionId: string;
  open: boolean;
  onClose: () => void;
  onCreated: (assignment: PlannedAssignment) => void;
}) {
  const [scope, setScope] = useState("wpw");
  const [contractorId, setContractorId] = useState("");
  const [expectedMonth, setExpectedMonth] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Tylko firmy z odpowiednimi AKTYWNYMI pozycjami umów ramowych dla zakresu —
  // ten sam leksykalny hard-filter, którym zamawianie filtruje firmy MDCP/projektowe.
  const { data: contractors } = useQuery({
    queryKey: ["pp", "planned-contractors", scope],
    staleTime: 5 * 60_000,
    enabled: open,
    queryFn: () => getPlannedAssignmentContractors(scope).then((r) => r.contractors),
  });

  const createM = useMutation({
    mutationFn: () =>
      createPlannedAssignment(connectionId, {
        scope,
        contractor_id: contractorId,
        expected_month: expectedMonth,
        note,
      }),
    onMutate: () => setError(null),
    onSuccess: (assignment) => {
      setContractorId("");
      setExpectedMonth("");
      setNote("");
      onCreated(assignment);
    },
    onError: (e) => setError(apiErr(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Oznacz jako planowane do zlecenia" size="sm">
      <div className="space-y-3">
        <Select
          label="Zakres przyszłego zlecenia"
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={(e) => {
            setScope(e.target.value);
            setContractorId(""); // lista firm zależy od zakresu
          }}
        />
        <SearchableSelect
          label="Podwykonawca"
          options={(contractors ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={contractorId}
          onChange={(e) => setContractorId(e.target.value)}
          placeholder="Wybierz firmę…"
        />
        <Input
          label="Przewidywany termin zlecenia"
          type="month"
          value={expectedMonth}
          onChange={(e) => setExpectedMonth(e.target.value)}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Notatka wewnętrzna (niewidoczna dla podwykonawcy)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm"
            placeholder="np. czekamy na wariant wiążący trasy…"
          />
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Zapowiedź nie wysyła powiadomień — firma zobaczy projekt na swojej mapie
          (zakres i liczniki, bez danych właścicieli) do czasu zamówienia albo wycofania.
        </p>
        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            size="sm"
            onClick={() => createM.mutate()}
            disabled={!contractorId || !expectedMonth || createM.isPending}
            loading={createM.isPending}
          >
            Oznacz jako planowane
          </Button>
        </div>
      </div>
    </Modal>
  );
}
